import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { formatTimestamp } from "@brainlog/shared";
import { errorMessage, get, post } from "@/lib/api";
import { qk, useMe, useNote } from "@/lib/queries";
import { peekEntry } from "./collab";
import type { EncryptedNoteSession } from "@/features/crypto/encryptedCollab";
import { decVersion } from "@/features/crypto/ops";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MarkdownPreview } from "./MarkdownPreview";
import { DiffView } from "@/components/DiffView";
import { getEditorBridge } from "./editorBridge";
import { cn } from "@/lib/utils";

const REASONS: Record<string, string> = {
  created: "Created",
  auto: "Saved automatically",
  manual: "Saved manually",
  restore: "Restored from an earlier version",
  "before-restore": "Saved before restoring",
  "ai-apply": "AI suggestion accepted",
  import: "Imported with a new version of the manuscript",
  "before-import": "Kept before importing a new version",
  agent: "Changed by an AI agent",
  "before-agent": "Kept before an AI agent's change",
  "manuscript-version": "Kept with a version of the manuscript",
  submission: "Kept at submission",
  "evidence-citation": "Cited as evidence",
  "study-run-snapshot": "Copy of the protocol kept for a run",
  "before-move": "Saved before moving",
  "before-ai-apply": "Saved before an AI suggestion",
  "before-references": "Kept before removing citations from the reference list",
  references: "Citations removed from the reference list",
};

export function VersionsDialog({ open, onOpenChange, noteId }: { open: boolean; onOpenChange(o: boolean): void; noteId: string }) {
  const me = useMe();
  const qc = useQueryClient();
  const note = useNote(noteId);
  const scopeId = note.data?.note.encryptionScopeId ?? null;
  const versions = useQuery({ queryKey: ["versions", noteId], queryFn: () => get<{ versions: any[] }>(`/api/notes/${noteId}/versions`) });
  const [selected, setSelected] = useState<string | null>(null);
  const [view, setView] = useState<"preview" | "diff" | "previous">("preview");
  const load = async (id: string) => {
    const r = await get<{ version: { id: string; versionNo: number; contentMd: string; title: string; createdAt: string; encrypted?: boolean } }>(`/api/notes/${noteId}/versions/${id}`);
    if (!r.version.encrypted || !scopeId) return r;
    // Encrypted history is decrypted in the browser.
    const v = await decVersion(scopeId, noteId, r.version.id, r.version.contentMd);
    return { version: { ...r.version, contentMd: v.text, title: v.title } };
  };
  const version = useQuery({ queryKey: ["version", noteId, selected, scopeId], queryFn: () => load(selected!), enabled: !!selected });
  // The version saved just before the selected one, to see what changed in it (and who changed it).
  const list = versions.data?.versions ?? [];
  const selectedNo = list.find((v) => v.id === selected)?.versionNo as number | undefined;
  const previousId = selectedNo === undefined ? undefined : list.filter((v) => v.versionNo < selectedNo).sort((a, b) => b.versionNo - a.versionNo)[0]?.id;
  const previous = useQuery({ queryKey: ["version", noteId, previousId, scopeId], queryFn: () => load(previousId!), enabled: !!previousId && view === "previous" });

  const restore = async () => {
    if (!selected || !version.data) return;
    if (!scopeId) return post<{ version: { versionNo: number } }>(`/api/notes/${noteId}/versions/${selected}/restore`);
    // Encrypted note: the restore is an ordinary (encrypted) edit made here, framed by two versions.
    const session = peekEntry(noteId) as EncryptedNoteSession | null;
    if (!session || session.kind !== "encrypted") throw new Error("Open the note (unlocked) to restore a version.");
    await session.saveVersion("before-restore");
    const ytext = session.doc.getText("content");
    session.doc.transact(() => {
      ytext.delete(0, ytext.length);
      ytext.insert(0, version.data!.version.contentMd);
    });
    return session.saveVersion("restore", selected);
  };
  const current = getEditorBridge(noteId)?.text() ?? "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-hidden sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
          <DialogDescription>
            Each version records who saved it. Compare a version with the one before it or with the current text; restoring creates a new version and earlier versions are never
            erased.{scopeId ? " This note is end-to-end encrypted: versions are decrypted here, in your browser." : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="grid min-h-0 gap-4 md:grid-cols-[18rem_1fr]">
          <ol className="max-h-[60vh] space-y-1 overflow-y-auto pr-1" aria-label="Versions">
            {versions.isLoading && <li className="text-sm text-muted-foreground">Loading…</li>}
            {versions.data?.versions.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => setSelected(v.id)}
                  aria-pressed={selected === v.id}
                  className={cn("w-full rounded-md border px-2 py-1.5 text-left text-sm hover:bg-accent", selected === v.id && "border-primary bg-accent")}
                >
                  <span className="font-medium">v{v.versionNo}</span> · {REASONS[v.reason] ?? v.reason}
                  <span className="block text-xs text-muted-foreground">
                    {formatTimestamp(v.createdAt, me.data?.timezone)} · {v.authorName ?? "System"}
                    {v.via ? ` via ${v.via}` : ""} · {v.encrypted ? "encrypted" : `${v.size} chars`}
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <div className="min-h-0 min-w-0">
            {!selected && <p className="text-sm text-muted-foreground">Select a version to preview it.</p>}
            {version.data && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex rounded-md border p-0.5" role="group" aria-label="Version view">
                    <Button size="sm" variant={view === "preview" ? "secondary" : "ghost"} onClick={() => setView("preview")} aria-pressed={view === "preview"}>
                      Preview
                    </Button>
                    <Button size="sm" variant={view === "previous" ? "secondary" : "ghost"} onClick={() => setView("previous")} aria-pressed={view === "previous"} disabled={!previousId}>
                      Changes in this version
                    </Button>
                    <Button size="sm" variant={view === "diff" ? "secondary" : "ghost"} onClick={() => setView("diff")} aria-pressed={view === "diff"}>
                      Compare with current
                    </Button>
                  </div>
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        const r = await restore();
                        if (!r) return;
                        toast.success(`Restored as new version v${r.version.versionNo}`);
                        await qc.invalidateQueries({ queryKey: ["versions", noteId] });
                        await qc.invalidateQueries({ queryKey: qk.note(noteId) });
                      } catch (e) {
                        toast.error(errorMessage(e));
                      }
                    }}
                  >
                    Restore this version
                  </Button>
                </div>
                <div className="max-h-[52vh] overflow-y-auto rounded-md border p-4">
                  {view === "preview" ? (
                    <MarkdownPreview markdown={version.data.version.contentMd} />
                  ) : view === "previous" ? (
                    previous.data ? (
                      <DiffView
                        before={previous.data.version.contentMd}
                        after={version.data.version.contentMd}
                        beforeLabel={`v${previous.data.version.versionNo}`}
                        afterLabel={`v${version.data.version.versionNo}`}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">{previousId ? "Loading the previous version…" : "This is the first version."}</p>
                    )
                  ) : (
                    <DiffView before={version.data.version.contentMd} after={current} beforeLabel={`v${version.data.version.versionNo}`} afterLabel="Current text" />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
